import { supabase, generateServiceLink } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { ShareLinks } from "./marketing/ShareLinks";
import { QRCodeSection } from "./marketing/QRCodeSection";
import { MetaTags } from "../shared/MetaTags";
import { generateQRCodeUrl, createStyledQRCode } from "@/utils/qrCode";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from 'react-i18next';
import saveAs from "file-saver";

export const Marketing = () => {
  const [storeUrl, setStoreUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [businessName, setBusinessName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [username, setUsername] = useState("");
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchStoreUrl = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('company_name, profile_image_url, service_page_link')
          .eq('id', user.id)
          .single();

        if (profile) {
          if (profile.company_name) {
            setBusinessName(profile.company_name);
          } else {
            setBusinessName("My Business");
          }
          
          if (profile.profile_image_url) {
            setProfileImage(profile.profile_image_url);
          }
          
          // Generate the service link using the helper function
          const serviceLink = generateServiceLink(user.id);
          setStoreUrl(serviceLink);

          // Use the existing service_page_link if available, otherwise use the generated one
          if (profile.service_page_link) {
            // Extract username from service_page_link (handles both "services/username" and "username" formats)
            const extractedUsername = profile.service_page_link.includes('/') 
              ? profile.service_page_link.split('/').pop() 
              : profile.service_page_link;
            setUsername(extractedUsername || user.id);
            // Always construct as "services/{username}" for consistency
            setStoreUrl(`services/${extractedUsername || user.id}`);
          } else {
            setStoreUrl(serviceLink);
            setUsername(user.id);
          }
        }
      }
      setLoading(false);
    };

    fetchStoreUrl();
  }, []);

  const getFullUrl = () => {
    if (!storeUrl) return "";
    
    const baseUrl = window.location.origin;
    return `${baseUrl}/${storeUrl}`;
  };

  // Generate dynamic OG URL for social sharing
  const getDynamicOGUrl = () => {
    if (!username) return getFullUrl();
    
    // TEMPORARILY: Use direct service page URL until OG function auth is fixed
    return getFullUrl();
    
    // TODO: Re-enable when auth is fixed
    // const baseUrl = window.location.origin;
    // const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    // return `${supabaseUrl}/functions/v1/dynamic-og?username=${encodeURIComponent(username)}`;
  };
  
  const fullUrl = getFullUrl();
  const dynamicOGUrl = getDynamicOGUrl();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(fullUrl);
    toast({
      title: t('marketing.linkCopied'),
      description: t('marketing.linkCopiedDesc'),
    });
  };

  const handleFacebookShare = () => {
    window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`,
        '_blank',
        'noopener,noreferrer'
    );
  };

  const handleInstagramEdit = () => {
    window.open('https://www.instagram.com/accounts/edit', '_blank', 'noopener,noreferrer');
  };

  const handleDownloadQR = async () => {
    try {
      if (!fullUrl) {
        toast({
          title: t('marketing.error'),
          description: t('marketing.cannotGenerateQR'),
          variant: "destructive"
        });
        return;
      }
      
      // Direct download of simple QR code
      const qrUrl = generateQRCodeUrl(fullUrl);
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      saveAs(blob, `${businessName.replace(/\s+/g, '-').toLowerCase()}-simple-qr.png`);
      
      toast({
        title: t('marketing.qrCodeDownloaded'),
        description: t('marketing.qrCodeDownloadedDesc'),
      });
    } catch (error) {
      console.error("Error downloading QR code:", error);
      toast({
        title: t('marketing.downloadFailed'),
        description: t('marketing.downloadFailedDesc'),
        variant: "destructive",
      });
    }
  };

  const handleDownloadStyledQR = async () => {
    try {
      if (!fullUrl) {
        toast({
          title: t('marketing.error'),
          description: t('marketing.cannotGenerateQR'),
          variant: "destructive"
        });
        return;
      }
      
      const qrUrl = generateQRCodeUrl(fullUrl);
      await createStyledQRCode(qrUrl, businessName, fullUrl);
      
      toast({
        title: t('marketing.styledQRCodeDownloaded'),
        description: t('marketing.styledQRCodeDownloadedDesc'),
      });
    } catch (error) {
      console.error("Error downloading styled QR code:", error);
      toast({
        title: t('marketing.downloadFailed'),
        description: t('marketing.downloadFailedDesc'),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6">
      <MetaTags
        title={businessName}
        description={`Check out our services at ${businessName}`}
        imageUrl={profileImage}
        url={fullUrl}
        siteName={businessName}
      />

      <ShareLinks
        storeUrl={fullUrl}
        onCopy={copyToClipboard}
        onFacebookShare={handleFacebookShare}
        onInstagramEdit={handleInstagramEdit}
      />

      <QRCodeSection
        fullUrl={fullUrl}
        businessName={businessName}
        onDownloadSimple={handleDownloadQR}
        onDownloadStyled={handleDownloadStyledQR}
      />
    </div>
  );
};
